/* eslint-disable max-lines */
import { MouseEvent, useEffect, useState, lazy, Suspense } from 'react'
import { Divider, Box, Spinner } from '@chakra-ui/react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import get from 'lodash/get'
import { observer } from 'mobx-react'
import { useForm, FormProvider } from 'react-hook-form'
import { toast } from 'react-toastify'
import { updateMediaById } from 'API/media'
import { updateTextContentById, deleteTextContentById, createTextContent, upsertMedia } from 'API/textContent'
import Button from 'components/Button'
import ModalDialog, { ModalDialogProps } from 'components/ModalDialog'
import { getDefaultFormValues } from 'components/Pages/ProcessDetailPage/utils/formValue'
import SvgIcon from 'components/SvgIcon'
import { ErrorMessageEnum } from 'constants/error'
import { BlockTextContentType } from 'constants/process'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import { ITextContentWithRelations } from 'interfaces/textContent'
import { getValidArray } from 'utils/common'
import { getRenderProcess, createDecisionPoint, clearOldDecisionPoint } from '../../../../utils'
import DecisionPoints from './components/DecisionPoints'
import IconSection from './components/IconSection'
import LinkedDialog from './components/LinkedDialog'
import MediaModal from './components/LinkedDialog/components/MediaModal'
import { BlockTextFormValues } from './enums'
import styles from './blockTextEditor.module.scss'

// Lazy load QuillEditor
const QuillEditor = lazy(() => import('./components/QuillEditor'))

interface BlockTextEditorProps extends ModalDialogProps {
  stepId: number
  blockText?: ITextContentWithRelations | null
  step: IStepWithRelations
  isEditMode: boolean
  handleShowAutosave: () => void
  handleShowManageMediaDialog: () => void
  handleShowEmbedLinkModal: () => void
  onActivity?: () => void
  onLockError?: (ownerName?: string) => void
}

const BlockTextEditor = ({
  blockText,
  step,
  isOpen,
  isEditMode,
  handleShowAutosave,
  handleShowManageMediaDialog,
  handleShowEmbedLinkModal,
  onActivity,
  onLockError,
  ...props
}: BlockTextEditorProps) => {
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      label: '',
      mediaList: [],
      mediaLabels: [],
      isDisableMediaLabel: false,
      iconId: 'no-icon'
    }
  })
  const { getValues, reset, register } = methods

  const { processStore, iconBuilderStore } = useStores()
  const { process } = processStore
  const [htmlContent, setHtmlContent] = useState<string>('')
  const [showMediaGallery, setShowMediaGallery] = useState(false)
  const [showDecisionPointDialog, setShowDecisionPointDialog] = useState(false)
  const [decisionPointIndex, setDecisionPointIndex] = useState<number>(-1)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showLinkInternalStep, setShowLinkInternalStep] = useState(false)
  const [showLinkExternalStep, setShowLinkExternalStep] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [mediaLink, setMediaLink] = useState<IMedia[]>([])
  const [selectedMedia, setSelectedMedia] = useState<IMedia[]>([])

  useEffect(() => {
    if (blockText?.id && isOpen) {
      const content = blockText?.content || ''
      setHtmlContent(content)
      const blockMedias = getValidArray(blockText?.textContentMedias).map(textContentMedia => textContentMedia?.media)
      setMediaLink(blockMedias)
      setSelectedMedia(blockMedias)
    }
  }, [blockText?.id, isOpen])

  useEffect(() => {
    getDefaultFormValues(reset, blockText as ITextContentWithRelations)
  }, [blockText, isOpen])

  useEffect(() => {
    register(BlockTextFormValues.ICON_ID)
  }, [register])

  async function handleUpdateTextContent(): Promise<void> {
    setLoading(true)
    try {
      const mediaLabels: IMedia[] = getValues(BlockTextFormValues.MEDIA_LABELS) ?? []
      const isDisableMediaLabel: boolean = getValues('isDisableMediaLabel') ?? false
      const mediaIds: number[] = getValidArray(mediaLink).map(media => media?.id)

      if (mediaLabels?.length > 0) {
        await Promise.all(
          getValidArray(mediaLabels).map(media => {
            return updateMediaById(media?.id, { name: media?.name })
          })
        )
      }

      await updateTextContentById(blockText?.id || '', {
        content: htmlContent,
        iconId: Number(get(getValues(), `${BlockTextFormValues.ICON_ID}`, 0)),
        isDisableMediaLabel
      })
      await upsertMedia(blockText?.id ?? '', mediaIds)
      await clearOldDecisionPoint(blockText as ITextContentWithRelations)
      await createDecisionPoint(getValues, Number(blockText?.id))
      onActivity?.()
      handleShowAutosave()
      if (props.toggle) {
        props.toggle({} as MouseEvent<any>)
      }
      getRenderProcess(process.id, processStore)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 403 || status === 409) {
        const ownerName = err?.response?.data?.error?.details?.editLockOwnerName as string | undefined
        onLockError?.(ownerName)
        return
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateTextContent(): Promise<void> {
    setLoading(true)
    try {
      const mediaIds: number[] = getValidArray(mediaLink).map(media => media?.id)
      const mediaLabels: IMedia[] = getValues(BlockTextFormValues.MEDIA_LABELS) ?? []
      const isDisableMediaLabel: boolean = getValues('isDisableMediaLabel') ?? false

      if (mediaLabels?.length > 0) {
        await Promise.all(
          getValidArray(mediaLabels).map(media => {
            return updateMediaById(media?.id, { name: media?.name })
          })
        )
      }
      const newTextContent = await createTextContent({
        type: BlockTextContentType.TEXT_BLOCK,
        content: htmlContent,
        stepId: step.id,
        position: Array.isArray(step?.textContents) ? step?.textContents?.length + 1 : 0,
        iconId: Number(get(getValues(), `${BlockTextFormValues.ICON_ID}`, 0)),
        isDisableMediaLabel
      })
      await upsertMedia(newTextContent?.id ?? '', mediaIds)
      await clearOldDecisionPoint(newTextContent as ITextContentWithRelations)
      await createDecisionPoint(getValues, Number(newTextContent?.id))
      onActivity?.()
      handleShowAutosave()
      if (props.toggle) {
        props.toggle({} as MouseEvent<any>)
      }
      getRenderProcess(process.id, processStore)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 403 || status === 409) {
        const ownerName = error?.response?.data?.error?.details?.editLockOwnerName as string | undefined
        onLockError?.(ownerName)
        return
      }
      toast.error(ErrorMessageEnum.TECHNICAL_ERROR)
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteBlock(): Promise<void> {
    try {
      await clearOldDecisionPoint(blockText as ITextContentWithRelations)
      await deleteTextContentById(blockText?.id || '')
      onActivity?.()
      handleShowAutosave()
      setShowDeleteDialog(false)
      if (props.toggle) {
        props.toggle({} as MouseEvent<any>)
      }
      getRenderProcess(process.id, processStore)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 403 || status === 409) {
        const ownerName = err?.response?.data?.error?.details?.editLockOwnerName as string | undefined
        onLockError?.(ownerName)
        setShowDeleteDialog(false)
        return
      }
      throw err
    }
  }

  function handleSaveMediaLinks(): void {
    setMediaLink(selectedMedia)
  }

  useEffect(() => {
    if (showMediaGallery) {
      setSelectedMedia(mediaLink)
    }
  }, [showMediaGallery])

  useEffect(() => {
    iconBuilderStore.fetchBlockIconList()
  }, [])

  return (
    <FormProvider {...methods}>
      <ModalDialog
        size="lg"
        title={isEditMode ? 'Edit instruction' : 'Add new instruction'}
        headerClassName={styles.dialogHeader}
        bodyClassName={styles.dialogBody}
        className={styles.editDialog}
        isOpen={isOpen}
        backdrop="static"
        {...props}
      >
        <Box marginX={6}>
          <div>
            <div className={styles.blockContent}>Instruction Content</div>
            <Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="200px"><Spinner /></Box>}>
              <QuillEditor
                value={htmlContent}
                onChange={setHtmlContent}
              />
            </Suspense>
          </div>
          <DecisionPoints
            setShowMediaGallery={setShowMediaGallery}
            showDecisionPointDialog={showDecisionPointDialog}
            setShowDecisionPointDialog={setShowDecisionPointDialog}
            setDecisionPointIndex={setDecisionPointIndex}
            setShowLinkInternalStep={setShowLinkInternalStep}
            setShowLinkExternalStep={setShowLinkExternalStep}
            step={step}
            mediaLink={mediaLink}
            setMediaLink={setMediaLink}
            blockText={blockText}
          />
          <IconSection />
        </Box>
        <Divider />
        <div className={cx(styles.actionButtons, styles.smallSpacingTop)}>
          {isEditMode && (
            <Button outline color="danger" onClick={() => setShowDeleteDialog(true)}>
              Delete Instruction
            </Button>
          )}
          <div className={cx({ [styles.saveGroup]: !isEditMode })}>
            <Button color="light" onClick={props.toggle}>
              Cancel
            </Button>
            <Button
              color="info"
              className={styles.saveBtn}
              onClick={() => {
                if (isEditMode) {
                  handleUpdateTextContent()
                }
                if (!isEditMode) {
                  handleCreateTextContent()
                }
              }}
              disabled={loading}
            >
              {isEditMode ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
        <LinkedDialog
          textContentId={blockText?.id || ''}
          isOpen={showDecisionPointDialog}
          setShowDecisionPointDialog={setShowDecisionPointDialog}
          isOpenLinkInternalStep={showLinkInternalStep}
          isOpenLinkExternalStep={showLinkExternalStep}
          setShowLinkInternalStep={setShowLinkInternalStep}
          setShowLinkExternalStep={setShowLinkExternalStep}
          step={step}
          decisionPointIndex={decisionPointIndex}
          handleShowManageMediaDialog={handleShowManageMediaDialog}
          handleShowEmbedLinkModal={handleShowEmbedLinkModal}
        />
        <MediaModal
          step={step}
          isOpen={showMediaGallery}
          onClose={() => setShowMediaGallery(false)}
          linkedMedia={selectedMedia}
          setLinkedMedia={setSelectedMedia}
          handleSave={handleSaveMediaLinks}
          handleShowManageMediaDialog={handleShowManageMediaDialog}
          handleShowEmbedLinkModal={handleShowEmbedLinkModal}
        />
        <ModalDialog
          isOpen={showDeleteDialog}
          toggle={() => setShowDeleteDialog(!showDeleteDialog)}
          headless
          className={styles.deleteDialog}
        >
          <SvgIcon size={48} iconName="deleteIcon" />
          <p className={styles.deleteTitle}>Are you sure you want to delete this Instruction?</p>
          <div className={styles.actionButtons}>
            <Button color="danger" onClick={handleDeleteBlock}>
              Yes, Delete
            </Button>
            <Button outline onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
          </div>
        </ModalDialog>
      </ModalDialog>
    </FormProvider>
  )
}

export default observer(BlockTextEditor)
